// Utility functions for image content hashing and duplicate detection

/**
 * Generate a content hash for a file using the Web Crypto API
 */
export const generateContentHash = async (file: File): Promise<string> => {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Check if an image with the same content hash already exists for the event
 */
export const checkImageDuplicate = async (
  supabase: any,
  eventId: string,
  eventType: string,
  contentHash: string
): Promise<{ isDuplicate: boolean; existingImage?: any; category?: string }> => {
  const { data, error } = await supabase
    .from('event_images')
    .select('*')
    .eq('event_id', eventId)
    .eq('event_type', eventType)
    .eq('content_hash', contentHash)
    .eq('is_active', true)
    .maybeSingle();

  if (error) {
    console.warn('Error checking for duplicates:', error);
    return { isDuplicate: false };
  }

  return {
    isDuplicate: !!data,
    existingImage: data,
    category: data?.category
  };
};

/**
 * Check if the same file path already exists in a different category
 */
export const checkFilePathConflict = async (
  supabase: any,
  eventId: string,
  eventType: string,
  filePath: string,
  targetCategory: string
): Promise<{ hasConflict: boolean; existingCategory?: string }> => {
  const { data, error } = await supabase
    .from('event_images')
    .select('category')
    .eq('event_id', eventId)
    .eq('event_type', eventType)
    .eq('file_path', filePath)
    .eq('is_active', true)
    .neq('category', targetCategory)
    .maybeSingle();

  if (error) {
    console.warn('Error checking file path conflict:', error);
    return { hasConflict: false };
  }

  return {
    hasConflict: !!data,
    existingCategory: data?.category
  };
};